import { css } from '@styled-system/css';
import { useEffect, useRef, useState } from 'react';

import { DotsIcon } from '@/shared/components/ui/DotsIcon';
import { Logo, type LogoVariant } from '@/shared/components/ui/Logo';

export interface InteractionOverlayItem {
  label: string;
  onClick: () => void;
  icon?: LogoVariant;
  danger?: boolean;
}

interface InteractionOverlayProps {
  items: InteractionOverlayItem[];
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export const InteractionOverlay = ({ items, orientation = 'horizontal', className }: InteractionOverlayProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Gestion du clic à l'extérieur et du scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Bloquer le scroll quand le menu est ouvert
      document.body.style.overflow = 'hidden';
    } else {
      // Rétablir le scroll quand le menu est fermé
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Calcul de la position du menu en fixed
  const [menuCoordinates, setMenuCoordinates] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && containerRef.current && menuRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current.offsetHeight;
      const menuWidth = menuRef.current.offsetWidth;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let top = containerRect.bottom + 8; // 8px de marge sous le bouton
      let left = containerRect.left;

      // Position verticale : si pas assez de place en dessous, afficher en haut
      if (containerRect.bottom + menuHeight + 8 > viewportHeight && containerRect.top - menuHeight - 8 > 0) {
        top = containerRect.top - menuHeight - 8;
      }

      // Position horizontale : ajuster si le menu dépasse à droite
      if (left + menuWidth > viewportWidth) {
        left = containerRect.right - menuWidth;
      }

      // S'assurer que le menu ne dépasse pas à gauche
      if (left < 8) {
        left = 8;
      }

      setMenuCoordinates({ top, left });
    }
  }, [isOpen]);

  const buttonStyles = css({
    display: 'flex',
    mt: '-0.3rem',
    ml: '-0.3rem',
    w: '1.5rem',
    h: '1.5rem',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'font.muted',
    userSelect: 'none',
    border: 'none',
    background: 'transparent',
    outline: 'none',

    _hover: {
      color: 'font.primary',
    },

    _focus: {
      outline: 'none',
    },
  });

  const menuStyles = css({
    position: 'fixed',
    bg: 'base.segBg',
    border: '0.0416rem solid',
    borderColor: 'border.default',
    rounded: 'sm',
    boxShadow: '0px 0.33rem 0.33rem 0px rgba(0, 0, 0, 0.25)',
    zIndex: 50,
    minW: '8.16rem',
    py: 'xs',
  });

  const itemStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: 'sm',
    px: 'xs',
    py: 'xs',
    rounded: 'xs',
    cursor: 'pointer',
    fontSize: 'xs',
    fontWeight: 'semibold',
    fontFamily: 'sans',
    color: 'font.primary',

    _hover: {
      bg: 'base.selectedBg',
    },
  });

  const dangerItemStyles = css({
    color: 'accent.red !important',
  });

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: InteractionOverlayItem) => {
    item.onClick();
    setIsOpen(false);
  };

  const containerStyles = css({
    position: 'relative',
    w: '0.82rem',
    h: '0.82rem',
  });

  return (
    <div ref={containerRef} className={`${containerStyles} ${className || ''}`}>
      <button type="button" onClick={handleToggle} className={buttonStyles} aria-expanded={isOpen} aria-haspopup="menu">
        <DotsIcon
          className={css({
            display: 'flex',
            alignItems: 'center',
            w: '0.9rem',
            h: '0.9rem',
          })}
          variant={orientation}
        />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={menuStyles}
          style={{
            top: `${menuCoordinates.top}px`,
            left: `${menuCoordinates.left}px`,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(item);
              }}
              className={`${itemStyles} ${item.danger ? dangerItemStyles : ''}`}
            >
              {item.icon && (
                <Logo
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    h: '0.8rem!important',
                    w: '0.8rem!important',
                    flexShrink: 0,
                  })}
                  variant={item.icon}
                  color={item.danger ? 'accent.red' : undefined}
                />
              )}
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
